import { Alert, Stack, Text } from '@mantine/core';
import { AlertTriangle, CheckCircle2, Loader } from 'lucide-react';
import { colors } from '../../theme/tokens';

/**
 * ValidatePane — renders the result of `useBuilderValidate`. Pure presentation:
 * the builder owns the query and passes loading + valid + errors/warnings in,
 * so this pane has no data dependency and is trivially testable.
 */
export interface ValidatePaneProps {
  isLoading: boolean;
  valid: boolean | undefined;
  errors: Array<{ path: string; message: string }> | undefined;
  warnings: Array<{ path: string; message: string }> | undefined;
}

export function ValidatePane({ isLoading, valid, errors, warnings }: ValidatePaneProps) {
  if (isLoading) {
    return (
      <Stack gap="sm" align="center" py="lg">
        <Loader size={20} color={colors.hudCyan} />
        <Text size="sm" c="dimmed">
          Validating YAML...
        </Text>
      </Stack>
    );
  }
  if (valid === undefined) return null;

  return (
    <Stack gap="sm">
      {valid ? (
        <Alert color="green" variant="light" icon={<CheckCircle2 size={14} />}>
          <Text fw={600} size="sm">
            Scenario is valid
          </Text>
        </Alert>
      ) : (
        <Alert color="red" variant="light" icon={<AlertTriangle size={14} />}>
          <Stack gap="xs">
            <Text fw={600} size="sm">
              Validation failed
            </Text>
            {errors && errors.length > 0 && (
              <Stack gap={4}>
                {errors.map((err, i) => (
                  <Text key={i} size="xs" c="red">
                    <strong>{err.path}:</strong> {err.message}
                  </Text>
                ))}
              </Stack>
            )}
          </Stack>
        </Alert>
      )}
      {warnings && warnings.length > 0 && (
        <Alert color="yellow" variant="light" icon={<AlertTriangle size={14} />}>
          <Stack gap="xs">
            <Text fw={600} size="sm">
              Warnings
            </Text>
            <Stack gap={4}>
              {warnings.map((warn, i) => (
                <Text key={i} size="xs" c="yellow">
                  <strong>{warn.path}:</strong> {warn.message}
                </Text>
              ))}
            </Stack>
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}
